<template>
  <div class="flex h-13">
    <div
      class="flex items-center bg-pure-white border-b border-l border-r border-slate-400 sticky left-0 z-10"
      :class="{ 'rounded-bl': isLast }"
    >
      <div class="px-4 flex flex-shrink-0 gap-2 bg-pure-white w-20">
        <BlTextBody2 :text="session.id" class="truncate text-slate-1000" />
      </div>
      <div
        class="px-4 flex flex-shrink-0 gap-2 bg-pure-white w-50"
        :title="session.name"
      >
        <BlTextBody2
          :text="session.name || 'Unnamed session'"
          class="truncate text-slate-1000"
        />
      </div>
    </div>
    <div
      class="px-4 flex-shrink-0 bg-pure-white w-40 flex items-center border-b border-slate-400"
    >
      <BlChip
        v-if="session.appointment_id && session.status === 'draft'"
        :chip="'scheduled'"
      />
      <BlChip v-else-if="session.status === 'ongoing'" :chip="'in_progress'" />
      <BlChip v-else :chip="session.status" />
    </div>
    <div
      class="px-4 flex-shrink-0 bg-pure-white w-60 flex items-center border-b border-slate-400"
    >
      <BlTextBody2 :text="sessionAppointment.timeslot" />
    </div>
    <div
      class="px-4 flex-shrink-0 bg-pure-white w-50 flex items-center border-b border-slate-400"
    >
      <BlTextBody2 v-if="!session.appointment || !session.appointment.user" text="-" />
      <BlTextBody2
        v-else
        :title="session.appointment.user.name || 'Deleted user'"
        :text="session.appointment.user.name || 'Deleted user'"
        class="truncate"
        :class="{ 'italic ': !session.appointment.user.name }"
      />
    </div>
    <div
      class="px-4 flex-shrink-0 bg-pure-white w-40 flex items-center border-b border-slate-400"
    >
      <BlTextBody2 :text="session.number_of_measurements" />
    </div>
    <div class="flex sticky right-0 bg-pure-white flex-grow">
      <div
        class="px-4 flex-shrink-0 bg-pure-white w-60 flex-grow flex items-center justify-end gap-2 border-b border-r border-slate-400"
        :class="{ 'rounded-br': isLast }"
      >
        <BlInputButton
          v-if="session.status === 'draft' && !session.appointment_id"
          type="outlined"
          dense
          icon="delete"
          @clicked="deleteOpened = true"
        />
        <router-link
          v-if="session.status === 'draft'"
          :to="`/session/${session.slug}/edit`"
        >
          <BlInputButton type="outlined" dense label="Edit session" />
        </router-link>
        <BlInputButton
          v-if="session.status === 'draft'"
          label="Start session"
          dense
          :loading="launchLoading"
          @clicked="onStartSession"
        />
        <router-link
          v-if="session.status === 'ongoing'"
          :to="`/session/${session.slug}`"
        >
          <BlInputButton label="Resume session" dense />
        </router-link>
      </div>
    </div>
    <BlBackdrop
      v-if="deleteOpened"
      title="Delete the draft"
      description="You are about to delete the session draft. Are you sure you want to proceed? This action is irreversible. "
      label="Delete"
      type="error"
      empty
      :disabled="deleteLoading"
      @backdrop-close="deleteOpened = false"
      @backdrop-action="onRemoveSession"
    />
    <LaunchBeforeTheTimeModal
      v-if="launchBeforeTheTimeOpened"
      :sessionAppointment="sessionAppointment"
      @close-modal="launchBeforeTheTimeOpened = false"
      @on-submit="onLaunchSession"
    />
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import moment from "moment";

import LaunchBeforeTheTimeModal from "../../sessions/draft/LaunchBeforeTheTimeModal.vue";

import BlTextBody2 from "../../BlTextBody2.vue";
import BlChip from "../../BlChip.vue";
import BlInputButton from "../../BlInputButton.vue";
import BlBackdrop from "../../BlBackdrop.vue";

export default {
  name: "SessionDraftItem",
  components: {
    LaunchBeforeTheTimeModal,
    BlTextBody2,
    BlChip,
    BlInputButton,
    BlBackdrop
  },
  props: {
    session: { type: Object, required: true },
    idx: { type: Number, required: true },
    isLast: { required: false }
    // @after-commit
  },
  data() {
    return {
      deleteOpened: false,
      deleteLoading: false,
      launchBeforeTheTimeOpened: false,
      launchLoading: false
    };
  },
  computed: {
    ...mapGetters([""]),
    sessionAppointment() {
      if (!this.session.appointment) {
        return {
          id: null,
          date: null,
          timeslot: "Unscheduled",
          user: null,
          supervisors: []
        };
      }

      const appointment = this.session.appointment;
      const date = moment(appointment.date).format("DD/MM/YY");
      const time = appointment.start_time_string
        ? `(${appointment.start_time_string} - ${appointment.end_time_string})`
        : "";
      const timeslot = `${date} ${time}`;

      return { ...appointment, timeslot };
    }
  },
  methods: {
    ...mapActions(["showToast", "updateSession", "deleteSession"]),
    onStartSession() {
      if (!this.session.number_of_measurements) {
        const title = `Can't start the session with zero target!`;
        this.showToast({ title, message: "", type: "error" });
        return;
      }

      if (!this.sessionAppointment.id) {
        return this.onLaunchSession();
      }

      const t = this.sessionAppointment.start_time_string || "24:00";
      const d = `${this.sessionAppointment.date}T${t}:00`;
      const startDate = moment(d);

      let showModal = false;
      if (moment().isBefore(startDate)) {
        showModal = true;
      }

      if (showModal) this.launchBeforeTheTimeOpened = true;
      else this.onLaunchSession();
    },
    onLaunchSession() {
      this.launchBeforeTheTimeOpened = false;
      this.launchLoading = true;
      const params = {
        id: this.session.id,
        session: { status: "ongoing" }
      };
      this.updateSession(params).then(({ success, data }) => {
        this.launchLoading = false;
        if (!success) return;
        this.$router.push(`/session/${data.slug}`);
      });
    },
    async onRemoveSession() {
      this.deleteLoading = true;
      await this.deleteSession(this.session);
      await this.$emit("after-commit");
      this.deleteLoading = false;
      this.deleteOpened = false;
    }
  }
};
</script>



// WEBPACK FOOTER //
// src/components/clients/session/DraftItem.vue